import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/Ionicons';

import { RootState, AppDispatch } from '@/store';
import { apiClient, handleApiError } from '@/config/api';
import { createConversation } from '@/store/slices/chatSlice';
import { colors, font, space, radius } from '@/theme/chalkCourt';

// Shape returned by /employee/directory — a minimal projection that
// deliberately omits PII (email, phone, DOB, address) the picker has no
// business handling. Don't widen this without revisiting the backend DTO.
interface StaffRow {
  id: number;
  fullName: string;
  departmentName?: string;
  admin?: boolean;
}

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

const NewConversationScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigation = useNavigation<any>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { isCreating } = useSelector((state: RootState) => state.chat);
  const myEmployeeId = user?.employee?.id;

  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [groupName, setGroupName] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    apiClient
      .get<StaffRow[]>('/employee/directory')
      .then((rows) => {
        if (!active) {
          return;
        }
        // Exclude self from the picker.
        setStaff(rows.filter((r) => r.id !== myEmployeeId));
        setLoading(false);
      })
      .catch((e) => {
        if (active) {
          setError(handleApiError(e));
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [myEmployeeId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return staff;
    }
    return staff.filter((s) => s.fullName.toLowerCase().includes(q));
  }, [staff, query]);

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const submit = async () => {
    if (selected.size === 0) {
      return;
    }
    setSubmitError(null);
    const ids = Array.from(selected);
    const isGroup = ids.length > 1;
    if (isGroup && !groupName.trim()) {
      setSubmitError('Give the group a name.');
      return;
    }
    const result = await dispatch(
      createConversation({
        type: isGroup ? 'GROUP' : 'DIRECT',
        name: isGroup ? groupName.trim() : undefined,
        memberEmployeeIds: ids,
      })
    );
    if (createConversation.fulfilled.match(result)) {
      const conv = result.payload;
      navigation.replace('Conversation', {
        twilioSid: conv.twilioConversationSid,
        title: isGroup
          ? conv.name
          : conv.members.find((m) => m.employeeId !== myEmployeeId)?.fullName,
      });
    } else {
      setSubmitError((result.payload as string) || 'Could not start the conversation.');
    }
  };

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator color={colors.accent} />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centered}>
        <Text style={styles.errorText}>{error}</Text>
      </View>
    );
  }

  const count = selected.size;
  const isGroup = count > 1;

  return (
    <View style={styles.screen}>
      <View style={styles.searchBox}>
        <Icon name="search" size={18} color={colors.inkFaint} />
        <TextInput
          style={styles.searchInput}
          value={query}
          onChangeText={setQuery}
          placeholder="Search staff…"
          placeholderTextColor={colors.inkFaint}
          autoCapitalize="none"
        />
      </View>

      {isGroup && (
        <TextInput
          style={styles.groupNameInput}
          value={groupName}
          onChangeText={setGroupName}
          placeholder="Group name (e.g. Saturday Crew)"
          placeholderTextColor={colors.inkFaint}
        />
      )}

      <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
        {filtered.length === 0 ? (
          <Text style={styles.noMatches}>No staff match "{query}".</Text>
        ) : (
          filtered.map((s) => {
            const isSelected = selected.has(s.id);
            return (
              <TouchableOpacity
                key={s.id}
                style={[styles.row, isSelected && styles.rowSelected]}
                activeOpacity={0.7}
                onPress={() => toggle(s.id)}>
                <View style={[styles.avatar, isSelected && styles.avatarSelected]}>
                  <Text style={[styles.avatarText, isSelected && styles.avatarTextSelected]}>
                    {initials(s.fullName)}
                  </Text>
                </View>
                <View style={styles.rowBody}>
                  <Text style={styles.rowTitle} numberOfLines={1}>
                    {s.fullName}
                  </Text>
                  {!!s.departmentName && (
                    <Text style={styles.rowSubtitle}>{s.departmentName}</Text>
                  )}
                </View>
                {isSelected && <Icon name="checkmark-circle" size={22} color={colors.accent} />}
              </TouchableOpacity>
            );
          })
        )}
      </ScrollView>

      <View style={styles.footer}>
        {submitError && <Text style={styles.submitError}>{submitError}</Text>}
        <TouchableOpacity
          style={[styles.startBtn, (count === 0 || isCreating) && styles.startBtnDisabled]}
          activeOpacity={0.85}
          disabled={count === 0 || isCreating}
          onPress={submit}>
          <Text style={styles.startBtnText}>
            {isCreating
              ? 'STARTING…'
              : count === 0
              ? 'PICK SOMEONE'
              : `START ${isGroup ? 'GROUP' : 'CHAT'} (${count})`}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  centered: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.bg },
  errorText: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.inkSoft,
    padding: space.lg,
    textAlign: 'center',
  },

  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    paddingHorizontal: space.md,
    margin: space.lg,
    marginBottom: space.sm,
  },
  searchInput: {
    flex: 1,
    height: 44,
    fontFamily: font.body,
    fontSize: 15,
    color: colors.ink,
    marginLeft: space.sm,
  },
  groupNameInput: {
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.line,
    paddingHorizontal: space.md,
    paddingVertical: space.sm,
    fontFamily: font.bodyMed,
    fontSize: 16,
    color: colors.ink,
    marginHorizontal: space.lg,
    marginBottom: space.sm,
  },

  list: { flex: 1 },
  listContent: { padding: space.lg, paddingTop: space.sm },
  noMatches: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.inkFaint,
    textAlign: 'center',
    paddingVertical: space.lg,
  },

  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.sm,
    borderWidth: 1,
    borderColor: colors.line,
  },
  rowSelected: {
    borderColor: colors.accent,
    backgroundColor: colors.accentSoft,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.surfaceSunk,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: space.md,
  },
  avatarSelected: { backgroundColor: colors.accent },
  avatarText: {
    fontFamily: font.bodyBold,
    fontSize: 14,
    color: colors.inkSoft,
  },
  avatarTextSelected: { color: colors.onAccent },
  rowBody: { flex: 1 },
  rowTitle: {
    fontFamily: font.bodyBold,
    fontSize: 15,
    color: colors.ink,
  },
  rowSubtitle: {
    fontFamily: font.bodyMed,
    fontSize: 12,
    color: colors.inkFaint,
    marginTop: 1,
  },

  footer: {
    padding: space.lg,
    borderTopWidth: 1,
    borderTopColor: colors.line,
    backgroundColor: colors.bg,
  },
  submitError: {
    fontFamily: font.bodyMed,
    fontSize: 13,
    color: colors.accentDark,
    marginBottom: space.sm,
    textAlign: 'center',
  },
  startBtn: {
    height: 54,
    borderRadius: radius.md,
    backgroundColor: colors.accent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  startBtnDisabled: { opacity: 0.4 },
  startBtnText: {
    fontFamily: font.display,
    fontSize: 21,
    letterSpacing: 1,
    color: colors.onAccent,
  },
});

export default NewConversationScreen;
